import {gql} from '@apollo/client';
import {StyleSheet, css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery, {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import deepCopy from 'lib/deepCopy';
import * as ComplexPolymerActions from 'modbio/complex-polymers/ComplexPolymerActions';
import {
  COMPLEX_POLYMER_DISPLAY_NAMES,
  ComplexPolymerConnectionType,
  ConnectionGraphQL,
  ResolvedAttachmentGraphQL,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  generateEntityAssociationsFromStructure,
  getConnectionInputs,
  getEntityIds,
  getExistingResolvedAttachmentInputs,
  getNextPolymerRef,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import useTagSchemaSummariesListingQuery from 'schemas/useTagSchemaSummariesListingQuery';
import {getTagSchemaOligoAwareFolderItemType} from 'tag-schema/util/TagSchemaUtil';
import {BLUE_LIGHT, GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

import {TagSchemaSummaryGraphQL} from '../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {ComplexPolymerType} from '../ComplexPolymerConstants';
import {
  generateConjugateStructureGraphNode,
  layoutUnconnectedConjugateNodes,
} from '../GeneratePolymerStructureGraphUtil';
import {PolymerStructureGraph, StructureGraphNode} from '../PolymerStructureGraphConstants';
import {
  ComplexPolymerAddConjugateQuery,
  ComplexPolymerAddConjugateQueryVariables,
} from './__generated/ComplexPolymerAddConjugateQuery';
import ComplexPolymerComponentSearchModal, {
  ComplexPolymerComponentSearchModalProps,
} from './ComplexPolymerComponentSearchModal';

export const COMPLEX_POLYMER_ADD_CONJUGATE_QUERY: DocumentNode = gql`
  query ComplexPolymerAddConjugateQuery($id: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    complexPolymer(id: $id) {
      ... on OligoConjugateNode {
        id
        conjugatesSchemaFields {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
            schemaId
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ... on OligoDuplexNode {
        id
        conjugatesSchemaFields {
          ... on ComplexPolymerSchemaFieldDefinitionNode {
            id
            schemaId
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function getComponentTypes(
  allSchemas: Array<TagSchemaSummaryGraphQL>,
  conjugateSchemaIds: Array<string>
): Array<ModelTypeConstants> {
  const conjugateSchemas = allSchemas.filter((tagSchema) => conjugateSchemaIds.includes(tagSchema.id));
  return conjugateSchemas.map((schema) => getTagSchemaOligoAwareFolderItemType(schema));
}
interface ComplexPolymerAddConjugateMenuProps {
  complexPolymerId: string;
  complexPolymerVersionId: string;
  complexPolymerType: ComplexPolymerType;
  simplePolymers: Array<SimplePolymerGraphQL>;
  connections: Array<ConnectionGraphQL>;
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>;
  structureGraph: PolymerStructureGraph | null;
  offset?: number;
}

export default function ComplexPolymerAddConjugateMenu({
  complexPolymerId,
  complexPolymerVersionId,
  complexPolymerType,
  simplePolymers,
  connections,
  resolvedAttachments,
  structureGraph,
  offset,
}: ComplexPolymerAddConjugateMenuProps): JSX.Element {
  const schemasQuery = useTagSchemaSummariesListingQuery();

  if (schemasQuery.type !== QueryResultType.SUCCESS) {
    return <BenchlingIcon className={css(styles.disabledAddIcon)} icon="edit" />;
  }

  return (
    <BenchlingQuery<ComplexPolymerAddConjugateQuery, ComplexPolymerAddConjugateQueryVariables>
      query={COMPLEX_POLYMER_ADD_CONJUGATE_QUERY}
      variables={{
        id: complexPolymerId,
      }}
      renderLoading={() => <BenchlingIcon className={css(styles.disabledAddIcon)} icon="edit" />}
    >
      {(data) => {
        assert(
          data.complexPolymer.__typename === 'OligoConjugateNode' ||
            data.complexPolymer.__typename === 'OligoDuplexNode',
          'Expected oligo conjugate or oligo duplex'
        );

        assert(
          data.complexPolymer.conjugatesSchemaFields != null,
          'Expected complex polymer to have conjugate schema fields'
        );

        const conjugateLinkSchemaIds: Array<string> = data.complexPolymer.conjugatesSchemaFields
          .flatMap((schemaField) =>
            schemaField != null && !isInaccessible(schemaField) ? [schemaField] : []
          )
          .map((schemaField) => {
            assert(schemaField.schemaId != null, 'Conjugates field on complex polymer must link to a schema');
            return schemaField.schemaId;
          });

        // We filter out all hydrogen bonds so that we don't duplicate the hydrogen bonds and the offset when
        // we send it to the backend.
        const connectionInputs = getConnectionInputs(connections).filter(
          (connectionInput) => connectionInput.connection_type !== ComplexPolymerConnectionType.HYDROGEN_BOND
        );
        const entityIds = getEntityIds(simplePolymers);
        const entityAssociations = generateEntityAssociationsFromStructure(simplePolymers);

        async function addConjugate(selectedIds: Array<string>): Promise<void> {
          const newEntityAssociations = deepCopy(entityAssociations);
          const newNodes: Array<StructureGraphNode> = [];

          for (const entityId of selectedIds) {
            const polymerRef = getNextPolymerRef(newEntityAssociations, entityId, complexPolymerType);

            if (structureGraph != null) {
              const newNode = generateConjugateStructureGraphNode(polymerRef, entityId);
              newNodes.push(newNode);
            }

            if (newEntityAssociations.hasOwnProperty(entityId)) {
              newEntityAssociations[entityId].push(polymerRef);
            } else {
              newEntityAssociations[entityId] = [polymerRef];
            }
          }

          const layoutededNewNodes =
            structureGraph != null ? layoutUnconnectedConjugateNodes(structureGraph.nodes, newNodes) : [];

          const updatedStructureGraph =
            structureGraph != null
              ? {
                  ...structureGraph,
                  nodes: structureGraph.nodes.concat(layoutededNewNodes),
                }
              : null;

          await ComplexPolymerActions.updateComplexPolymer(
            complexPolymerId,
            complexPolymerVersionId,
            [...entityIds, ...selectedIds],
            connectionInputs,
            getExistingResolvedAttachmentInputs(resolvedAttachments),
            newEntityAssociations,
            complexPolymerType,
            offset,
            updatedStructureGraph ?? undefined
          );
        }

        const componentTypes = getComponentTypes(
          schemasQuery.data.tagSchemasListing.tagSchemas,
          conjugateLinkSchemaIds
        );

        return (
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT}>
                <Option
                  onSelect={() => {
                    runModal<ComplexPolymerComponentSearchModalProps>(ComplexPolymerComponentSearchModal, {
                      schemaIds: conjugateLinkSchemaIds,
                      componentTypes,
                      onSubmit: addConjugate,
                      complexPolymerTypeDescriptor: COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType],
                      titleText: 'Add components',
                      submitButtonText: 'Add',
                      rowSelectionMode: RowSelectionMode.MULTIPLE,
                    });
                  }}
                >
                  Add components
                </Option>
              </Menu>
            }
          >
            <BenchlingIcon className={css(styles.addIcon)} icon="edit" />
          </Dropdown>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  addIcon: {
    cursor: 'pointer',
    color: BLUE_LIGHT,
    marginLeft: '5px',
  },
  disabledAddIcon: {
    color: GRAY_LIGHT,
    marginLeft: '5px',
  },
});
